import { z } from 'zod';
import { t as tGlobal } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';

export enum BusinessCapitalOptions {
  BUSINESS_REVENUE = 'BUSINESS_REVENUE',
  OWNER_CONTRIBUTIONS = 'OWNER_CONTRIBUTIONS',
  INVESTMENT_CAPITAL = 'INVESTMENT_CAPITAL',
  SALE_OF_ASSETS = 'SALE_OF_ASSETS',
  BUSINESS_LOAN = 'BUSINESS_LOAN',
  GRANTS = 'GRANTS',
  OTHER = 'OTHER',
}

export const useBusinessCapitalLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');

  // List of options for the form
  const optionsList = [
    {
      value: BusinessCapitalOptions.BUSINESS_REVENUE,
      getLabel: () => t('organic.businessCapital.businessRevenue'),
    },
    {
      value: BusinessCapitalOptions.OWNER_CONTRIBUTIONS,
      getLabel: () => t('organic.businessCapital.ownerContributions'),
    },
    {
      value: BusinessCapitalOptions.INVESTMENT_CAPITAL,
      getLabel: () => t('organic.businessCapital.investmentCapital'),
    },
    {
      value: BusinessCapitalOptions.SALE_OF_ASSETS,
      getLabel: () => t('organic.businessCapital.saleOfAssets'),
    },
    {
      value: BusinessCapitalOptions.BUSINESS_LOAN,
      getLabel: () => t('organic.businessCapital.businessLoan'),
    },
    {
      value: BusinessCapitalOptions.GRANTS,
      getLabel: () => t('organic.businessCapital.grants'),
    },
    {
      value: BusinessCapitalOptions.OTHER,
      getLabel: () => t('organic.businessCapital.other'),
    },
  ];

  const formMethods = useFormHook<{
    [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE]: string[];
    [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT]: string;
  }>({
    schemaTitle: 'Business Capital Source',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE]: (() => {
        const value = getDefaultValueByQuestionId(EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE);
        if (!Array.isArray(value)) return [];
        return value.filter((item: string) => item);
      })(),
      [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT]:
        getDefaultValueByQuestionId(
          EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT
        ) || '',
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE]: z
          .array(z.string())
          .min(1, { message: tGlobal('enErrors:required') }),
        [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT]: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        // Check if OTHER option is selected
        const isOtherSelected = data[EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE].includes(
          BusinessCapitalOptions.OTHER
        );

        // Only validate OTHER_FREE_TEXT field when OTHER is selected
        if (isOtherSelected) {
          const freeText = data[EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT];

          if (!freeText || freeText.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tGlobal('enErrors:required'),
              path: [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT],
            });
          } else if (freeText.length < 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(
                'organic.businessCapital.validation.minLength',
                'Value must be at least 10 characters'
              ),
              path: [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT],
            });
          } else if (freeText.length > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(
                'organic.businessCapital.validation.maxLength',
                'Value must be less than 100 characters'
              ),
              path: [EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    /** Save to local store */
    saveEddQuestionToLocalStore({
      question: t('organic.businessCapital.subtitle'),
      questionId: EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE,
      answer: JSON.stringify([
        ...formMethods.getValues(EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE),
      ]),
    });

    saveEddQuestionToLocalStore({
      question: `${t('organic.businessCapital.subtitle')} - other free text`,
      questionId: EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT,
      answer: JSON.stringify(
        formMethods.getValues(EddEmbeddedFields.EDD_BUSINESS_CAPITAL_SOURCE_OTHER_FREE_TEXT)
      ),
    });

    /** Save to Server */
    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_BUSINESS_CAPITAL);
    } catch (error) {
      /** TODO: Handle error - Check with PM */
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
    optionsList,
  };
};
